import { useState } from 'react';
import {
  BIEN_TYPE_LABELS,
  ELEMENT_KIND_LABELS,
  STRUCTURE_KIND_LABELS,
  StructureKindSchema,
  type BienDto,
  type ElementDto,
  type NiveauDto,
  type StructureDto,
} from '../../lib/api-types';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader } from '../../components/ui/Card';
import { Dialog } from '../../components/ui/Dialog';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { ElementQuantities } from './ElementQuantities';
import {
  useCreateBienElement,
  useCreateEspaceElement,
  useCreateNiveau,
  useCreateStructure,
  useElements,
  useNiveaux,
  useStructures,
} from './hooks';

/** One bien of the dossier: header + expandable structure tree. */
export function BienCard({ bien }: { bien: BienDto }): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold text-slate-900">{bien.designation}</h3>
          <Badge tone="info">{BIEN_TYPE_LABELS[bien.type]}</Badge>
          {bien.superficie ? (
            <span className="text-sm text-slate-500">{bien.superficie} m²</span>
          ) : null}
          {bien.reference ? (
            <span className="font-mono text-xs text-slate-400">{bien.reference}</span>
          ) : null}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Replier' : 'Détailler'}
        </Button>
      </CardHeader>
      {expanded ? <BienDetails bien={bien} /> : null}
    </Card>
  );
}

function BienDetails({ bien }: { bien: BienDto }): JSX.Element {
  const structuresQuery = useStructures(bien.id);
  const elementsQuery = useElements(bien.id);
  const [addingStructure, setAddingStructure] = useState(false);
  const [addingExterior, setAddingExterior] = useState(false);

  const exteriorElements = (elementsQuery.data ?? []).filter((e) => !e.niveauId);

  return (
    <CardContent className="space-y-5">
      {/* Structures (bâtiments) */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Structures
          </h4>
          <Button type="button" size="sm" onClick={() => setAddingStructure(true)}>
            Ajouter une structure
          </Button>
        </div>
        {structuresQuery.isLoading ? (
          <Spinner label="Chargement des structures…" />
        ) : structuresQuery.isError ? (
          <Alert variant="error">{toDisplayError(structuresQuery.error).message}</Alert>
        ) : structuresQuery.data && structuresQuery.data.length > 0 ? (
          <ul className="space-y-3">
            {structuresQuery.data.map((s) => (
              <StructureNode key={s.id} structure={s} elements={elementsQuery.data ?? []} />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">Aucune structure pour ce bien.</p>
        )}
      </section>

      {/* Ouvrages extérieurs / plus-values (rattachés au bien, sans niveau) */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Ouvrages extérieurs / plus-values
          </h4>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => setAddingExterior(true)}
          >
            Ajouter un ouvrage / une plus-value
          </Button>
        </div>
        {elementsQuery.isLoading ? (
          <Spinner label="Chargement des éléments…" />
        ) : elementsQuery.isError ? (
          <Alert variant="error">{toDisplayError(elementsQuery.error).message}</Alert>
        ) : exteriorElements.length > 0 ? (
          <ul className="space-y-2">
            {exteriorElements.map((e) => (
              <ElementRow key={e.id} element={e} />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">Aucun ouvrage extérieur ni plus-value.</p>
        )}
      </section>

      <StructureCreateDialog
        bienId={bien.id}
        open={addingStructure}
        onClose={() => setAddingStructure(false)}
      />
      <ExteriorElementCreateDialog
        bienId={bien.id}
        open={addingExterior}
        onClose={() => setAddingExterior(false)}
      />
    </CardContent>
  );
}

function StructureNode({
  structure,
  elements,
}: {
  structure: StructureDto;
  elements: ElementDto[];
}): JSX.Element {
  const niveauxQuery = useNiveaux(structure.id);
  const [addingNiveau, setAddingNiveau] = useState(false);

  return (
    <li className="rounded-md border border-slate-200 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-slate-800">{structure.libelle}</span>
          <Badge tone="neutral">{STRUCTURE_KIND_LABELS[structure.kind]}</Badge>
          {structure.type ? <span className="text-xs text-slate-500">{structure.type}</span> : null}
        </div>
        <Button type="button" size="sm" variant="outline" onClick={() => setAddingNiveau(true)}>
          Ajouter un niveau
        </Button>
      </div>

      <div className="mt-3 border-l-2 border-slate-100 pl-3">
        {niveauxQuery.isLoading ? (
          <Spinner label="Chargement des niveaux…" />
        ) : niveauxQuery.isError ? (
          <Alert variant="error">{toDisplayError(niveauxQuery.error).message}</Alert>
        ) : niveauxQuery.data && niveauxQuery.data.length > 0 ? (
          <ul className="space-y-3">
            {niveauxQuery.data.map((n) => (
              <NiveauNode
                key={n.id}
                niveau={n}
                elements={elements.filter((e) => e.niveauId === n.id)}
              />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">Aucun niveau pour cette structure.</p>
        )}
      </div>

      <NiveauCreateDialog
        structureId={structure.id}
        open={addingNiveau}
        onClose={() => setAddingNiveau(false)}
      />
    </li>
  );
}

function NiveauNode({
  niveau,
  elements,
}: {
  niveau: NiveauDto;
  elements: ElementDto[];
}): JSX.Element {
  const [addingEspace, setAddingEspace] = useState(false);

  return (
    <li>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-slate-700">{niveau.libelle}</span>
        <Button type="button" size="sm" variant="ghost" onClick={() => setAddingEspace(true)}>
          Ajouter un espace
        </Button>
      </div>
      {elements.length > 0 ? (
        <ul className="mt-2 space-y-2">
          {elements.map((e) => (
            <ElementRow key={e.id} element={e} />
          ))}
        </ul>
      ) : (
        <p className="mt-1 text-sm text-slate-500">Aucun espace sur ce niveau.</p>
      )}

      <EspaceCreateDialog
        niveauId={niveau.id}
        open={addingEspace}
        onClose={() => setAddingEspace(false)}
      />
    </li>
  );
}

function ElementRow({ element }: { element: ElementDto }): JSX.Element {
  const [showQuantities, setShowQuantities] = useState(false);

  return (
    <li className="rounded-md border border-slate-200 px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-800">{element.libelle}</span>
          <Badge tone={element.kind === 'ESPACE' ? 'neutral' : 'warning'}>
            {ELEMENT_KIND_LABELS[element.kind]}
          </Badge>
          {element.spaceType ? (
            <span className="text-xs text-slate-500">{element.spaceType}</span>
          ) : null}
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          aria-expanded={showQuantities}
          onClick={() => setShowQuantities((v) => !v)}
        >
          Quantités
        </Button>
      </div>
      {showQuantities ? (
        <div className="mt-2">
          <ElementQuantities elementId={element.id} />
        </div>
      ) : null}
    </li>
  );
}

// --- Create dialogs -------------------------------------------------------------

function FormError({ error }: { error: unknown }): JSX.Element | null {
  if (!error) return null;
  const display = toDisplayError(error);
  return (
    <Alert variant="error" details={display.details}>
      {display.message}
    </Alert>
  );
}

function StructureCreateDialog({
  bienId,
  open,
  onClose,
}: {
  bienId: string;
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const create = useCreateStructure(bienId);
  const [libelle, setLibelle] = useState('');
  const [type, setType] = useState('');
  const [kind, setKind] = useState<'PRINCIPAL' | 'ANNEXE'>('PRINCIPAL');
  const [localError, setLocalError] = useState('');

  const submit = () => {
    if (!libelle.trim()) {
      setLocalError('Le libellé est requis.');
      return;
    }
    setLocalError('');
    create.mutate(
      { libelle: libelle.trim(), type: type.trim() || undefined, kind },
      {
        onSuccess: () => {
          setLibelle('');
          setType('');
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} title="Ajouter une structure">
      <div className="space-y-4">
        <FormError error={create.isError ? create.error : null} />
        <div>
          <Label htmlFor="structure-libelle">Libellé</Label>
          <Input
            id="structure-libelle"
            value={libelle}
            aria-invalid={Boolean(localError)}
            onChange={(e) => setLibelle(e.target.value)}
          />
          {localError ? <p className="mt-1 text-sm text-red-600">{localError}</p> : null}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="structure-kind">Nature</Label>
            <Select
              id="structure-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as 'PRINCIPAL' | 'ANNEXE')}
            >
              {StructureKindSchema.options.map((k) => (
                <option key={k} value={k}>
                  {STRUCTURE_KIND_LABELS[k]}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="structure-type">Type (facultatif)</Label>
            <Input id="structure-type" value={type} onChange={(e) => setType(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={create.isPending}>
            Annuler
          </Button>
          <Button type="button" onClick={submit} loading={create.isPending}>
            Créer la structure
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function NiveauCreateDialog({
  structureId,
  open,
  onClose,
}: {
  structureId: string;
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const create = useCreateNiveau(structureId);
  const [libelle, setLibelle] = useState('');
  const [localError, setLocalError] = useState('');

  const submit = () => {
    if (!libelle.trim()) {
      setLocalError('Le libellé est requis.');
      return;
    }
    setLocalError('');
    create.mutate(
      { libelle: libelle.trim() },
      {
        onSuccess: () => {
          setLibelle('');
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} title="Ajouter un niveau">
      <div className="space-y-4">
        <FormError error={create.isError ? create.error : null} />
        <div>
          <Label htmlFor="niveau-libelle">Libellé</Label>
          <Input
            id="niveau-libelle"
            placeholder="ex. Rez-de-chaussée"
            value={libelle}
            aria-invalid={Boolean(localError)}
            onChange={(e) => setLibelle(e.target.value)}
          />
          {localError ? <p className="mt-1 text-sm text-red-600">{localError}</p> : null}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={create.isPending}>
            Annuler
          </Button>
          <Button type="button" onClick={submit} loading={create.isPending}>
            Créer le niveau
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function EspaceCreateDialog({
  niveauId,
  open,
  onClose,
}: {
  niveauId: string;
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const create = useCreateEspaceElement(niveauId);
  const [libelle, setLibelle] = useState('');
  const [description, setDescription] = useState('');
  const [spaceType, setSpaceType] = useState('');
  const [localError, setLocalError] = useState('');

  const submit = () => {
    if (!libelle.trim()) {
      setLocalError('Le libellé est requis.');
      return;
    }
    setLocalError('');
    create.mutate(
      {
        kind: 'ESPACE',
        libelle: libelle.trim(),
        description: description.trim() || undefined,
        spaceType: spaceType.trim() || undefined,
      },
      {
        onSuccess: () => {
          setLibelle('');
          setDescription('');
          setSpaceType('');
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} title="Ajouter un espace">
      <div className="space-y-4">
        <FormError error={create.isError ? create.error : null} />
        <div>
          <Label htmlFor="espace-libelle">Libellé</Label>
          <Input
            id="espace-libelle"
            placeholder="ex. Salon"
            value={libelle}
            aria-invalid={Boolean(localError)}
            onChange={(e) => setLibelle(e.target.value)}
          />
          {localError ? <p className="mt-1 text-sm text-red-600">{localError}</p> : null}
        </div>
        <div>
          <Label htmlFor="espace-spaceType">Type d’espace (facultatif)</Label>
          <Input
            id="espace-spaceType"
            value={spaceType}
            onChange={(e) => setSpaceType(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="espace-description">Description (facultatif)</Label>
          <Input
            id="espace-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={create.isPending}>
            Annuler
          </Button>
          <Button type="button" onClick={submit} loading={create.isPending}>
            Créer l’espace
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function ExteriorElementCreateDialog({
  bienId,
  open,
  onClose,
}: {
  bienId: string;
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const create = useCreateBienElement(bienId);
  const [kind, setKind] = useState<'OUVRAGE' | 'PLUS_VALUE'>('OUVRAGE');
  const [libelle, setLibelle] = useState('');
  const [description, setDescription] = useState('');
  const [localError, setLocalError] = useState('');

  const submit = () => {
    if (!libelle.trim()) {
      setLocalError('Le libellé est requis.');
      return;
    }
    setLocalError('');
    create.mutate(
      { kind, libelle: libelle.trim(), description: description.trim() || undefined },
      {
        onSuccess: () => {
          setLibelle('');
          setDescription('');
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} title="Ajouter un ouvrage extérieur / une plus-value">
      <div className="space-y-4">
        <FormError error={create.isError ? create.error : null} />
        <div>
          <Label htmlFor="exterior-kind">Nature</Label>
          <Select
            id="exterior-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as 'OUVRAGE' | 'PLUS_VALUE')}
          >
            <option value="OUVRAGE">{ELEMENT_KIND_LABELS.OUVRAGE}</option>
            <option value="PLUS_VALUE">{ELEMENT_KIND_LABELS.PLUS_VALUE}</option>
          </Select>
        </div>
        <div>
          <Label htmlFor="exterior-libelle">Libellé</Label>
          <Input
            id="exterior-libelle"
            placeholder="ex. Clôture"
            value={libelle}
            aria-invalid={Boolean(localError)}
            onChange={(e) => setLibelle(e.target.value)}
          />
          {localError ? <p className="mt-1 text-sm text-red-600">{localError}</p> : null}
        </div>
        <div>
          <Label htmlFor="exterior-description">Description (facultatif)</Label>
          <Input
            id="exterior-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={create.isPending}>
            Annuler
          </Button>
          <Button type="button" onClick={submit} loading={create.isPending}>
            Créer l’élément
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
